/**
 * Connection enterprise forçada no `/authorize`.
 *
 * O Universal Login é compartilhado por todos os clientes, então a connection de
 * um deles não pode virar botão na tela de login — apareceria para a base
 * inteira. E a descoberta por domínio da Auth0 depende do Identifier First, que
 * é ajuste de tenant e mudaria o login de todos. Sobra o dashboard dizer qual
 * connection usar, a partir de uma URL de entrada (`/sso/<connection>`) que a
 * equipe do cliente guarda.
 *
 * A escolha fica lembrada, para que o acesso seguinte pelo endereço normal não
 * caia no login por senha — mas só depois de um login bem-sucedido. Até lá ela
 * fica pendente, de modo que um link forjado não muda o login de ninguém.
 */

const CONFIRMED_KEY = 'ssoConnection'
const PENDING_KEY = 'ssoConnectionPending'

export const SSO_ENTRY_PATH = '/sso'

// Nome de connection na Auth0 é alfanumérico com hífen e underscore. O valor vai
// para a query string do /authorize, então nada fora disso é aceito.
const VALID_CONNECTION = /^[A-Za-z0-9_-]{1,128}$/

const read = (key: string): string | null => {
  try {
    const value = window.localStorage.getItem(key)
    return value && VALID_CONNECTION.test(value) ? value : null
  } catch {
    return null
  }
}

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // Navegador sem storage: o SSO continua funcionando pela URL de entrada,
    // só não é lembrado.
  }
}

export const isSsoEntryPath = (pathname: string): boolean =>
  pathname === SSO_ENTRY_PATH || pathname.startsWith(`${SSO_ENTRY_PATH}/`)

export const connectionFromPath = (pathname: string): string | null => {
  if (!isSsoEntryPath(pathname)) return null

  const raw = pathname.slice(SSO_ENTRY_PATH.length + 1).split('/')[0] || ''
  if (!raw) return null

  let candidate: string
  try {
    candidate = decodeURIComponent(raw)
  } catch {
    return null
  }

  return VALID_CONNECTION.test(candidate) ? candidate : null
}

/**
 * Parâmetros extras do `/authorize`. A URL de entrada manda; sem ela, vale a
 * connection confirmada por um login anterior neste navegador.
 */
export const ssoAuthorizeParams = (pathname: string): { connection: string } | undefined => {
  const fromPath = connectionFromPath(pathname)

  if (fromPath) {
    write(PENDING_KEY, fromPath)
    return { connection: fromPath }
  }

  const confirmed = read(CONFIRMED_KEY)
  return confirmed ? { connection: confirmed } : undefined
}

/** Promove a connection pendente depois que o login de fato aconteceu. */
export const confirmSsoConnection = (): void => {
  const pending = read(PENDING_KEY)
  if (!pending) return

  write(CONFIRMED_KEY, pending)
  write(PENDING_KEY, null)
}

export const clearSsoConnection = (): void => {
  write(CONFIRMED_KEY, null)
  write(PENDING_KEY, null)
}
